import { LitElement, html, css } from 'lit';
import importedQuickInfo from '../assets/svg/quick-info.svg';
import importedArrowRight from '../assets/svg/product-detail-card-arrow-right.svg';
import importedCross from '../assets/svg/cross.svg';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

const quickInfo = `data:image/svg+xml;base64,${btoa(importedQuickInfo)}`;
const arrowRight = `data:image/svg+xml;base64,${btoa(importedArrowRight)}`;
const cross = `data:image/svg+xml;base64,${btoa(importedCross)}`;

class CbomProductDetailCard extends LitElement {
  // Properties
  static properties = {
    isMobile: { type: Boolean },
    id: { type: Number }, // product
    category: { type: Object },
    title: { type: String },
    src: { type: String },
    keyFeatures: { type: Array },
    shortDescription: { type: String },
    price: { type: Number },
    url: { type: String },
    translations: { type: Object },
    productOffsetTop: { type: Number },
    configuratorOffsetTop: { type: Number },
  };

  // Styles
  static styles = css`
    :host {
      z-index: 61;
      box-sizing: border-box;
      font-family: 'Arial';
      font-style: normal;
      color: #000000;
      font-weight: 400;
      font-size: 14px;
      position: absolute;
      left: -388px;
    }

    .mobile-price,
    h3 {
      font-weight: 700;
      font-size: 22px;
      line-height: 25px;
      color: #434649;
      margin: 0 0 12px;
    }

    ul,
    p {
      line-height: 20px;
    }

    button {
      border-radius: 4px;
    }

    button.action {
      border: 1px solid #434649;
      display: block;
      width: 100%;
      font-weight: 700;
      font-size: 15px;
      line-height: 17px;
      text-transform: uppercase;
      margin-top: 16px;
      padding: 13px;
      cursor: pointer;
      transition-property: background, color, border;
      transition-duration: 200ms;
      transition-timing-function: ease-out;
      background: transparent;
    }

    .is-mobile .action {
      padding: 17px;
    }

    .card.is-mobile button.more-details {
      background: transparent;
    }

    button.more-details:hover {
      background: hsl(0deg 0% 90%);
    }

    button.close {
      font-size: 12px;
      border: none;
      position: absolute;
      font-weight: 700;
      padding: 7px 0;
      line-height: 1;
      top: 0px;
      width: 45px;
      text-align: center;
      right: 0;
      cursor: pointer;
      background: transparent;
    }

    button.product-select {
      border: none;
      background: #1c9ace;
      color: #fff;
    }

    button.product-select:hover {
      background-color: hsl(198deg 76% 40%);
    }

    .product-image {
      max-height: 225px;
      margin: 0 auto;
      margin-bottom: 14px;
      border-radius: 4px;
      width: 215px;
      height: 215px;
    }

    .image-wrap {
      text-align: center;
      background: #fff;
      border-radius: 4px;
    }

    .arrow {
      position: absolute;
      width: 45px;
      height: 45px;
      left: 388px;
      z-index: 2;
    }

    h4 {
      display: flex;
      align-items: center;
      justify-content: left;
      margin: 1em 0 0;
    }

    h4 img {
      margin-right: 5px;
    }

    ul {
      margin: 0 0 16px;
    }

    li {
      line-height: 20px;
    }

    .card {
      position: absolute;
      box-shadow: 0px 5px 25px 0px rgba(0, 0, 0, 0.16);
      border-radius: 8px 8px 0 0;
      z-index: 1;
      padding: 24px;
      box-sizing: border-box;
      background: #f4f4f4;
      width: 388px;
    }

    .card,
    .arrow {
      transition-property: top;
      transition-duration: 150ms;
      transition-timing-function: ease-out;
    }

    .card.is-mobile {
      position: fixed;
      background: #ffffff;
      padding: 0 40px 40px;
      bottom: -100%;
      left: 0;
      width: 100%;
      text-align: center;
      transition: bottom 250ms ease-out;
    }

    .card.is-mobile.is-open {
      bottom: 0;
    }

    .overwash {
      position: fixed;
      inset: 0;
      opacity: 0;
      background: rgba(0, 0, 0, 0.66);
      z-index: 1;
      transition: opacity 150ms ease-out;
    }

    .overwash.is-open {
      opacity: 1;
    }

    .body {
      max-height: 270px;
      overflow-y: scroll;
    }
  `;

  constructor() {
    super();
    this._closeDelay = 400;
  }

  _onCloseClick = () => {
    const customEvent = new CustomEvent('cbomproductdetailcardcloseclick');

    // Removes open class, allows transition to play, then dispatches close event
    if (this.isMobile) {
      this.shadowRoot.querySelector('.card').classList.remove('is-open');
      this.shadowRoot.querySelector('.overwash').classList.remove('is-open');
      setTimeout(() => {
        this.dispatchEvent(customEvent);
      }, 250);

      // Desktop just fires the event
    } else {
      this.dispatchEvent(customEvent);
    }
  };

  _onSelectProductClick = () => {
    this.dispatchEvent(
      new CustomEvent('cbomproductdetailcardselectproductclick', {
        detail: {
          id: this.id,
          category: this.category,
        },
      })
    );
  };

  _onViewProductPageClick = () => {
    window.open(this.url, '_blank').focus();
  };

  _renderKeyFeatures() {
    if (!this.isMobile) {
      return html`
        <h4>
          <img src=${quickInfo} alt="" />
          ${this.translations.keyFeatures}
        </h4>
        <ul>
          ${this.keyFeatures.split('\n').map((keyFeature) => {
            return html`<li>${keyFeature}</li>`;
          })}
        </ul>
      `;
    }
  }

  _renderSelectThisProduct() {
    if (this.isMobile) {
      return html`
        <button
          @click=${this._onSelectProductClick}
          class="action product-select"
        >
          ${this.translations.selectThisProduct}
        </button>
      `;
    }
  }

  _renderShortDescription() {
    if (!this.isMobile) {
      return unsafeHTML(this.shortDescription);
    }
  }

  // Render the arrow on desktop pointing to the product
  _renderArrow() {
    if (!this.isMobile) {
      return html`<span class="arrow"><img src=${arrowRight} alt="" /></span>`;
    }
  }

  _onCardMouseenter = () => {
    if (!this.isMobile) {
      clearTimeout(window.mouseLeaveTimeout);
    }
  };

  _onCardMouseleave = () => {
    if (!this.parent && !this.isMobile) {
      clearTimeout(window.mouseLeaveTimeout);
      window.mouseLeaveTimeout = setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent('cbomproductcardmouseleave', {
            detail: {
              id: this.id,
              category: this.category,
            },
          })
        );
      }, this._closeDelay);
    }
  };

  _getDesktopCardTop() {
    // Spacer from the window top
    const topPadding = 30;

    // Current window scroll position
    const windowTopPos =
      document.documentElement.scrollTop || document.body.scrollTop;

    // Difference between hovered product and window scroll position
    const productWindowDiff = this.productOffsetTop - windowTopPos;

    // Difference between product hovered and the configurator top
    const productConfiguratorDiff =
      this.productOffsetTop - this.configuratorOffsetTop;

    // Push up card as high as it can go
    let cardHeightAdjustment = 500;
    if (cardHeightAdjustment > productWindowDiff) {
      cardHeightAdjustment = productWindowDiff; // About half of the cards height
    }

    // Position the card with information gathered
    let cardTop = productConfiguratorDiff - cardHeightAdjustment + topPadding;

    return cardTop;
  }

  // Once card is rendered remove the is-open class then add it again
  // after a short delay - this is to trigger the transitions
  updated() {
    const isOpenClass = 'is-open';
    const card = this.shadowRoot.querySelector('.card');
    const arrow = this.shadowRoot.querySelector('.arrow');
    const overwash = this.shadowRoot.querySelector('.overwash');
    setTimeout(() => {
      card.classList.remove(isOpenClass);
      if (arrow) {
        arrow.classList.remove(isOpenClass);
      }
      if (overwash) {
        overwash.classList.remove(isOpenClass);
      }
    }, 1);
    setTimeout(() => {
      card.classList.add(isOpenClass);
      if (arrow) {
        arrow.classList.add(isOpenClass);
      }
      if (overwash) {
        overwash.classList.add(isOpenClass);
      }
    }, 50);
  }

  render() {
    return html`
      <!-- dynamic styling that applies wherever it renders -->
      <style>
        .card {
          ${this.isMobile ? null : `top: ${this._getDesktopCardTop()}px`};
        }

        .arrow {
          ${this.isMobile
          ? null
          : `top: ${this.productOffsetTop - this.configuratorOffsetTop}px`};
        }
      </style>

      ${this.isMobile
        ? html`<div class="overwash" @click=${this._onCloseClick}></div>`
        : null}
      ${this._renderArrow()}

      <div
        @mouseenter=${this._onCardMouseenter}
        @mouseleave=${this._onCardMouseleave}
        class="card${this.isMobile ? ' is-mobile' : null}"
      >
        ${this.isMobile
          ? html`<button class="close" @click=${this._onCloseClick}>
              <img src=${cross} alt="${this.translations.close}" />
            </button>`
          : null}
        ${this.isMobile ? null : html`<h3>${this.title}</h3>`}

        <!-- desktop extend bg -->
        <div class="image-wrap">
          <img class="product-image" src="${this.src}" alt="${this.title}" />
        </div>

        ${this.isMobile
          ? html`
              <h3>${this.title}</h3>
              <div class="mobile-price">${unsafeHTML(this.price)}</div>
            `
          : null}

        <div class="body">
          ${this._renderKeyFeatures()} ${this._renderShortDescription()}
        </div>

        ${this._renderSelectThisProduct()}

        <button
          class="action more-details"
          @click=${this._onViewProductPageClick}
        >
          ${this.isMobile
            ? this.translations.viewProductPage
            : this.translations.moreDetails}
        </button>
      </div>
    `;
  }
}

customElements.define('cbom-product-detail-card', CbomProductDetailCard);
